import {
  Badge,
  Box,
  createStyles,
  lighten,
  makeStyles,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Add, DragIndicator } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { ContentActionGq } from '../../../../../graphql/projects/models/ContentActionGq';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { ContentKeywordSearchTableState } from './logic/useContentKeywordSearchTableState';
import TableCellUrl from './TableCellUrl';

export interface ContentKeywordAddTableRowProps {
  contentKeyword: ContentKeywordGq;
  clientUrl: string;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  isDragginGlobal?: boolean;
  tableState: ContentKeywordSearchTableState;
  selectBag: {
    deactivateDragMode: () => void;
    onMouseOver: (keywordIndex: number) => void;
    onMouseDown: (contentKeywordId: number, keywordIndex: number) => void;
    isIndexSelected: (index: number) => boolean;
  };
  index: number;
  project: ProjectGq;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: 'pointer',
      userSelect: 'none',
    },
    selectMode: {
      backgroundColor: lighten(theme.palette.secondary.light, 0.8),
    },
    selectModeHover: {
      '&:hover': { backgroundColor: lighten(theme.palette.secondary.light, 0.6) + '!important' },
    },
    dragButton: {
      cursor: 'grab',
      marginTop: '5px',
      position: 'relative',
    },
    dragIcon: {},
    addIcon: {
      fontSize: '16px',
      fontWeight: 500,
      position: 'absolute',
      left: '10px',
      top: '6.5px',
      zIndex: 10,
      color: theme.palette.grey[500],
    },
    tableCellFifty: {
      width: '50%',
    },
    tableCellTen: {
      width: '10%',
    },
    tableCellFive: {
      width: '5%',
    },
    dragging: {
      display: 'table',
    },
    draggingGroup: {
      backgroundColor: lighten(theme.palette.secondary.light, 0.6) + '!important',
    },
    naText: {
      color: theme.palette.grey[400],
    },
  })
);

export const ContentKeywordAddTableRow: React.FC<ContentKeywordAddTableRowProps> = ({
  contentKeyword,
  clientUrl,
  provided,
  snapshot,
  isDragginGlobal,
  tableState,
  selectBag,
  index,
  project,
}) => {
  // Styling
  const theme = useTheme();
  const classes = useClasses(theme);

  const isSelected = tableState.selectionState.contentKeywordIds[contentKeyword.id];
  const selectedItems = tableState.backendSelectionState.contentKeywordIds;

  /* We use div component because droppable is a div and wouldn't be valid if it's <Table> html */
  return (
    <TableRow
      component="div"
      key={contentKeyword.id}
      onMouseDown={
        selectBag.onMouseDown ? () => selectBag.onMouseDown(contentKeyword.id, index) : undefined
      }
      onMouseOver={selectBag.onMouseOver ? () => selectBag.onMouseOver(index) : undefined}
      onMouseUp={selectBag.deactivateDragMode}
      hover={!isDragginGlobal}
      selected={selectBag.isIndexSelected(index)}
      className={clsx({
        [classes.tableRow]: true,
        [classes.selectMode]: isSelected && !isDragginGlobal,
        [classes.dragging]: snapshot.isDragging,
        [classes.draggingGroup]: snapshot.isDragging && isSelected,
      })}
      classes={{
        hover: clsx({
          [classes.selectModeHover]: isSelected,
        }),
      }}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <TableCell padding={'none'} className={classes.tableCellFive} component="div">
        {/* The badge is the little number that appears while dragging, indicating how many rows are being dragged */}
        <Badge
          badgeContent={selectedItems.length}
          color="primary"
          invisible={!(snapshot.isDragging && isSelected && selectedItems.length > 1)}
        >
          <Box
            pl={0.25}
            {...provided.dragHandleProps}
            className={classes.dragButton}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onMouseUp={(event) => {
              event.stopPropagation();
            }}
          >
            <DragIndicator className={classes.dragIcon} fontSize={'small'} color={'disabled'} />
            <Add className={classes.addIcon} />
          </Box>
        </Badge>
      </TableCell>
      <TableCell component="div" className={classes.tableCellFifty}>
        {contentKeyword.keyword}
      </TableCell>
      {project.hasEnglishTranslation && (
        <TableCell component="div" align="right" className={classes.tableCellTen}>
          {contentKeyword.keywordEnglish}
        </TableCell>
      )}
      <TableCell component="div" align="right" className={classes.tableCellTen}>
        {formatNumber(contentKeyword.volume)}
      </TableCell>
      <TableCell component="div" align="right" className={classes.tableCellTen}>
        {contentKeyword.taskAction === ContentActionGq.CREATE ? (
          <Typography variant={'body2'} className={classes.naText}>
            N/A
          </Typography>
        ) : !!contentKeyword.rank ? (
          contentKeyword.rank
        ) : (
          '100+'
        )}
      </TableCell>
      <TableCellUrl component="div" contentKeyword={contentKeyword} clientUrl={clientUrl} />
    </TableRow>
  );
};

export default ContentKeywordAddTableRow;
